import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import PropTypes from 'prop-types';
import AuthContext from './authContext';
import permissionService from '../services/permissionService';
import {
	IPermissionActionState,
	IPermissionItem,
	IPermissionsMap,
	IUserAccessItem,
} from '../type/permission-type';
import { getMenuMetadataForPermission } from '../config/menuRouteConfig';
import { applySidebarSections, ISidebarSection } from '../config/sidebarMenu';
import { dashboardPagesMenu } from '../menu';
import { ENV } from '../config/env.config';
import { resolveHomePath } from '../helpers/homePath';
import PERMISSION_KEYS from '../constants/permissionKeys';

export interface IPermissionContextProps {
	permissions: IPermissionItem[];
	userAccess: IUserAccessItem[];
	permissionsMap: IPermissionsMap;
	dynamicMenu: Record<string, any>;
	homePath: string;
	isLoadingPermissions: boolean;
	hasPermission: (permissionKeyOrName: string, action?: string) => boolean;
	canRead: (permissionKeyOrName: string) => boolean;
	canCreate: (permissionKeyOrName: string) => boolean;
	canUpdate: (permissionKeyOrName: string) => boolean;
	canCancel: (permissionKeyOrName: string) => boolean;
	canDelete: (permissionKeyOrName: string) => boolean;
	canResetPassword: (permissionKeyOrName: string) => boolean;
	refetchPermissions: () => Promise<void>;
}

export const PermissionContext = createContext<IPermissionContextProps>(
	{} as IPermissionContextProps,
);

interface IPermissionContextProviderProps {
	children: ReactNode;
}

export const PermissionContextProvider: FC<IPermissionContextProviderProps> = ({ children }) => {
	const { authUser, isLoading: isAuthLoading } = useContext(AuthContext);
	const [permissions, setPermissions] = useState<IPermissionItem[]>([]);
	const [sidebarSections, setSidebarSections] = useState<ISidebarSection[]>([]);
	const [isFetchingPermissions, setIsFetchingPermissions] = useState<boolean>(false);
	const [loadedForUserId, setLoadedForUserId] = useState<number | null>(null);
	const loadedForUserIdRef = React.useRef<number | null>(null);

	// FETCH PERMISSIONS DIRECTLY FROM /api/v1/permission (CONTAINS ACCESS OBJECT & TREE)
	const refetchPermissions = useCallback(async () => {
		const token = localStorage.getItem(ENV.TOKEN_KEY);
		if (!token) {
			setPermissions([]);
			setSidebarSections([]);
			setLoadedForUserId(null);
			loadedForUserIdRef.current = null;
			setIsFetchingPermissions(false);
			return;
		}

		if (!authUser?.id) {
			return;
		}

		const userId = Number(authUser.id);
		setIsFetchingPermissions(true);
		try {
			const { permissions: allPerms, sidebarSections: sections } =
				await permissionService.getAllPermissions();
			setPermissions(allPerms);
			setSidebarSections(sections);
			setLoadedForUserId(userId);
			loadedForUserIdRef.current = userId;
		} catch (error) {
			console.error('Error loading permissions:', error);
			setPermissions([]);
			setSidebarSections([]);
			setLoadedForUserId(userId);
			loadedForUserIdRef.current = userId;
		} finally {
			setIsFetchingPermissions(false);
		}
	}, [authUser?.id]);

	useEffect(() => {
		const userId = authUser?.id ? Number(authUser.id) : null;
		if (!userId) {
			setPermissions([]);
			setSidebarSections([]);
			setLoadedForUserId(null);
			loadedForUserIdRef.current = null;
			setIsFetchingPermissions(false);
			return;
		}
		if (loadedForUserIdRef.current !== userId) {
			setPermissions([]);
			setSidebarSections([]);
			refetchPermissions();
		}
	}, [refetchPermissions, authUser?.id]);

	const token = localStorage.getItem(ENV.TOKEN_KEY);
	const permissionsReadyForUser = Boolean(
		authUser?.id && loadedForUserId === Number(authUser.id),
	);
	const isLoadingPermissions = Boolean(
		token &&
			(isAuthLoading ||
				!authUser ||
				isFetchingPermissions ||
				!permissionsReadyForUser),
	);

	// NORMALIZE PERMISSIONS AND ACCESS OBJECTS INTO A FAST LOOKUP MAP
	const permissionsMap = useMemo<IPermissionsMap>(() => {
		const map: IPermissionsMap = {};

		const indexPermission = (p: IPermissionItem) => {
			if (!p) return;

			// EXTRACT ACCESS BOOLEANS (e.g. access: { create: true, read: true, update: true, delete: true, reset_password: true })
			const accessObj: Record<string, any> = { ...(p.access || {}) };

			// Handle actions array if present (could be array of objects or strings)
			if (Array.isArray(p.actions)) {
				p.actions.forEach((act: any) => {
					if (typeof act === 'string') {
						accessObj[act.toLowerCase().trim()] = true;
					} else if (typeof act === 'object' && act !== null) {
						Object.entries(act).forEach(([k, v]) => {
							accessObj[k.toLowerCase().trim()] = Boolean(v);
						});
					}
				});
			}

			// Handle permissions array if present
			if (Array.isArray(p.permissions)) {
				p.permissions.forEach((permKey: string) => {
					if (typeof permKey === 'string') {
						accessObj[permKey.toLowerCase().trim()] = true;
					}
				});
			}

			const readVal = Boolean(
				accessObj.read || (p as any).read || accessObj.view || (p as any).view,
			);
			const createVal = Boolean(
				accessObj.create || (p as any).create || accessObj.add || (p as any).add,
			);
			const updateVal = Boolean(
				accessObj.update ||
					accessObj.edit ||
					(p as any).update ||
					(p as any).edit ||
					accessObj.write ||
					(p as any).write,
			);
			const deleteVal = Boolean(
				accessObj.delete || (p as any).delete || accessObj.remove || (p as any).remove,
			);
			const resetPasswordVal = Boolean(
				accessObj.reset_password ||
					accessObj.resetPassword ||
					accessObj['reset-password'] ||
					(p as any).reset_password ||
					(p as any).resetPassword ||
					(p as any)['reset-password'],
			);

			const state: IPermissionActionState = {
				id: p.id,
				name: p.name,
				permission_key: p.permission_key,
				...accessObj,
				read: readVal,
				create: createVal,
				update: updateVal,
				delete: deleteVal,
				reset_password: resetPasswordVal,
				resetPassword: resetPasswordVal,
			};

			const idStr = String(p.id).trim();
			if (idStr) {
				map[idStr] = state;
			}
			if (p.permission_key) {
				map[p.permission_key.toLowerCase().trim()] = state;
			}
			if (p.name) {
				map[p.name.toLowerCase().trim()] = state;
			}

			if (Array.isArray(p.children) && p.children.length > 0) {
				p.children.forEach(indexPermission);
			}
		};

		permissions.forEach(indexPermission);

		return map;
	}, [permissions]);

	// HELPER PERMISSION CHECKS
	const hasPermission = useCallback(
		(permissionKeyOrName: string, action = 'read'): boolean => {
			if (!permissionKeyOrName) return false;
			const normalized = permissionKeyOrName.toLowerCase().trim();
			const alternate = normalized.endsWith('s')
				? normalized.slice(0, -1)
				: `${normalized}s`;
			const underscore = normalized.replace(/[-\s]+/g, '_');
			const dash = normalized.replace(/[_\s]+/g, '-');
			const space = normalized.replace(/[_-]+/g, ' ');

			const perm =
				permissionsMap[normalized] ||
				permissionsMap[alternate] ||
				permissionsMap[underscore] ||
				permissionsMap[dash] ||
				permissionsMap[space];

			if (!perm) return false;

			const actNorm = action.toLowerCase().trim();
			if (
				actNorm === 'reset_password' ||
				actNorm === 'resetpassword' ||
				actNorm === 'reset-password'
			) {
				return Boolean(
					perm.reset_password ||
						perm.resetPassword ||
						perm['reset-password'] ||
						perm[action],
				);
			}

			return Boolean(perm[action] || perm[actNorm]);
		},
		[permissionsMap],
	);

	const canRead = useCallback(
		(permissionKeyOrName: string): boolean => hasPermission(permissionKeyOrName, 'read'),
		[hasPermission],
	);

	const canCreate = useCallback(
		(permissionKeyOrName: string): boolean => hasPermission(permissionKeyOrName, 'create'),
		[hasPermission],
	);

	const canUpdate = useCallback(
		(permissionKeyOrName: string): boolean => hasPermission(permissionKeyOrName, 'update'),
		[hasPermission],
	);

	const canCancel = useCallback(
		(permissionKeyOrName: string): boolean => hasPermission(permissionKeyOrName, 'cancel'),
		[hasPermission],
	);

	const canDelete = useCallback(
		(permissionKeyOrName: string): boolean => hasPermission(permissionKeyOrName, 'delete'),
		[hasPermission],
	);

	const canResetPassword = useCallback(
		(permissionKeyOrName: string): boolean =>
			hasPermission(permissionKeyOrName, 'reset_password'),
		[hasPermission],
	);

	// DYNAMICALLY GENERATE MENU BASED ON PERMISSIONS AND USER READ ACCESS (INCLUDING NESTED CHILDREN)
	const dynamicMenu = useMemo<Record<string, any>>(() => {
		if (!authUser) {
			return {};
		}

		if (permissions.length === 0) {
			return {};
		}

		// NORMALIZE PERMISSIONS INTO HIERARCHICAL TREE (SUPPORTING DIRECT CHILDREN OR parent_id)
		const permissionTree: IPermissionItem[] = [];
		const permMapById: Record<string, IPermissionItem> = {};

		permissions.forEach((p) => {
			permMapById[String(p.id)] = {
				...p,
				children: Array.isArray(p.children) ? [...p.children] : [],
			};
		});

		permissions.forEach((p) => {
			const item = permMapById[String(p.id)];
			if (p.parent_id && permMapById[String(p.parent_id)]) {
				const parent = permMapById[String(p.parent_id)];
				if (!parent.children) {
					parent.children = [];
				}
				if (!parent.children.some((c) => String(c.id) === String(item.id))) {
					parent.children.push(item);
				}
			} else if (!p.parent_id) {
				if (!permissionTree.some((r) => String(r.id) === String(item.id))) {
					permissionTree.push(item);
				}
			}
		});

		// RECURSIVE MENU BUILDER
		const buildMenuItem = (perm: IPermissionItem): any | null => {
			if (perm.is_show === false || (perm.status && perm.status !== 'ACTIVE')) return null;
			const key = perm.permission_key || perm.name;
			const hasDirectRead = canRead(key) || canRead(perm.name);
			const menuKey = String(perm.menu_key || '');
			const isProcessWork =
				Boolean(perm.is_virtual) && menuKey.startsWith('process_work_');
			const processWorkId =
				isProcessWork
					? String(perm.process_id || menuKey.replace('process_work_', '') || '').trim()
					: '';

			if (perm.show_in_menu === false && !perm.is_virtual && !isProcessWork) {
				if (!Array.isArray(perm.children) || !perm.children.length) return null;
			}

			// PROCESS CHILDREN RECURSIVELY
			let subMenuObj: Record<string, any> | null = null;
			if (Array.isArray(perm.children) && perm.children.length > 0) {
				const sortedChildren = [...perm.children].sort((a, b) => {
					const orderA = a.display_order !== undefined ? Number(a.display_order) : 999;
					const orderB = b.display_order !== undefined ? Number(b.display_order) : 999;
					return orderA - orderB;
				});

				const childMenu: Record<string, any> = {};
				sortedChildren.forEach((child) => {
					const childItem = buildMenuItem(child);
					if (childItem) {
						const childKey =
							child.menu_key ||
							child.permission_key ||
							child.name ||
							String(child.id);
						childMenu[childKey] = childItem;
					}
				});

				if (Object.keys(childMenu).length > 0) {
					subMenuObj = childMenu;
				}
			}

			if (perm.show_in_menu === false && !perm.is_virtual && !isProcessWork) {
				return null;
			}

			// IF PERMITTED DIRECTLY OR HAS AT LEAST ONE PERMITTED CHILD
			if (hasDirectRead || subMenuObj !== null) {
				const metadata = getMenuMetadataForPermission(key, perm.name);
				const resolvedSubMenu = subMenuObj;

				let resolvedPath: string | null = '';
				if (isProcessWork && processWorkId) {
					resolvedPath = `production/process-work/${processWorkId}`;
				} else if (metadata?.path !== undefined) {
					resolvedPath = metadata.path;
				} else if (resolvedSubMenu !== null) {
					resolvedPath = null;
				}

				if (metadata || resolvedSubMenu !== null || isProcessWork) {
					// Prefer menuRouteConfig icons (by permission key / name) over DB icon —
					// seed often stores the same VerifiedUser for every child.
					const resolvedIcon = isProcessWork
						? 'AccountTree'
						: metadata?.icon || perm.icon || 'ListAlt';
					return {
						id: perm.id || key,
						text: isProcessWork ? perm.name : metadata?.text || perm.name,
						path: resolvedPath,
						icon: resolvedIcon,
						subMenu: resolvedSubMenu,
					};
				}
			}

			return null;
		};

		// SORT ROOT PERMISSIONS BY DISPLAY ORDER
		const sortedRoots = permissionTree.sort((a, b) => {
			const orderA = a.display_order !== undefined ? Number(a.display_order) : 999;
			const orderB = b.display_order !== undefined ? Number(b.display_order) : 999;
			return orderA - orderB;
		});

		const menuObj: Record<string, any> = {};
		sortedRoots.forEach((rootPerm) => {
			const item = buildMenuItem(rootPerm);
			if (item) {
				const rootKey = rootPerm.permission_key || rootPerm.name || String(rootPerm.id);
				menuObj[rootKey] = item;
			}
		});

		if (Object.keys(menuObj).length > 0) {
			return applySidebarSections(menuObj, sidebarSections);
		}

		if (canRead(PERMISSION_KEYS.DASHBOARD) || canRead('dashboard')) {
			return dashboardPagesMenu;
		}

		return menuObj;
	}, [authUser, permissions, sidebarSections, canRead]);

	const homePath = useMemo(
		() => resolveHomePath(canRead, dynamicMenu),
		[canRead, dynamicMenu],
	);

	const value: IPermissionContextProps = useMemo(
		() => ({
			permissions,
			userAccess: [],
			permissionsMap,
			dynamicMenu,
			homePath,
			isLoadingPermissions,
			hasPermission,
			canRead,
			canCreate,
			canUpdate,
			canCancel,
			canDelete,
			canResetPassword,
			refetchPermissions,
		}),
		[
			permissions,
			permissionsMap,
			dynamicMenu,
			homePath,
			isLoadingPermissions,
			hasPermission,
			canRead,
			canCreate,
			canUpdate,
			canCancel,
			canDelete,
			canResetPassword,
			refetchPermissions,
		],
	);

	return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

PermissionContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default PermissionContext;
